import React from 'react';
import './Form.css';

/**
 * Form component with ArbiWin design system integration
 * Main form container with responsive layouts and consistent styling
 * Based on registration form design pattern
 *
 * @param {Object} props - Form properties
 * @param {Function} props.onSubmit - Form submit handler
 * @param {React.ReactNode} props.children - Form content
 * @param {string} props.layout - Form layout: 'default', 'centered', 'card', 'split'
 * @param {string} props.size - Form size: 'small', 'medium', 'large', 'full'
 * @param {boolean} props.loading - Show loading state
 * @param {boolean} props.disabled - Disable entire form
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.testId - Test ID for testing
 * @param {boolean} props.noValidate - Disable HTML5 validation
 * @returns {JSX.Element} Form component
 */
const Form = function Form({
  onSubmit,
  children,
  layout = 'default',
  size = 'medium',
  loading = false,
  disabled = false,
  className = '',
  testId,
  noValidate = true,
  ...rest
}) {

  const formClasses = [
    'arbiwin-form',
    'arbiwin-form--layout-' + layout,
    'arbiwin-form--size-' + size,
    loading ? 'arbiwin-form--loading' : '',
    disabled ? 'arbiwin-form--disabled' : '',
    className
  ].filter(Boolean).join(' ');

  const handleSubmit = function(event) {
    if (event) {
      event.preventDefault();
    }

    if (loading || disabled) {
      return;
    }

    if (onSubmit) {
      onSubmit(event);
    }
  };

  return (
    <form
      className={formClasses}
      onSubmit={handleSubmit}
      noValidate={noValidate}
      data-testid={testId || 'arbiwin-form'}
      {...rest}
    >
      {loading && (
        <div className="arbiwin-form__loading-overlay" aria-hidden="true">
          <div className="arbiwin-form__loading-spinner">
            <svg viewBox="0 0 50 50" className="arbiwin-form__loading-icon">
              <circle
                cx="25"
                cy="25"
                r="20"
                fill="none"
                stroke="currentColor"
                strokeWidth="4"
                strokeDasharray="125.66"
                strokeDashoffset="125.66"
                strokeLinecap="round"
              />
            </svg>
          </div>
        </div>
      )}
      <div className="arbiwin-form__content">
        {children}
      </div>
    </form>
  );
};

/**
 * FormContainer component for wrapping forms with consistent styling
 * Matches the registration form container design
 *
 * @param {Object} props - FormContainer properties
 * @param {React.ReactNode} props.children - Container content
 * @param {boolean} props.centered - Center the container
 * @param {boolean} props.fullWidth - Make container full width
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.testId - Test ID for testing
 * @returns {JSX.Element} FormContainer component
 */
const FormContainer = function FormContainer({
  children,
  centered = true,
  fullWidth = false,
  className = '',
  testId
}) {

  const containerClasses = [
    'arbiwin-form-container',
    centered ? 'arbiwin-form-container--centered' : '',
    fullWidth ? 'arbiwin-form-container--full-width' : '',
    className
  ].filter(Boolean).join(' ');

  return (
    <div
      className={containerClasses}
      data-testid={testId || 'arbiwin-form-container'}
    >
      {children}
    </div>
  );
};

/**
 * FormHeader component for form titles and descriptions
 *
 * @param {Object} props - FormHeader properties
 * @param {string} props.title - Form title
 * @param {string} props.subtitle - Form subtitle/description
 * @param {React.ReactNode} props.children - Custom header content
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.testId - Test ID for testing
 * @returns {JSX.Element} FormHeader component
 */
const FormHeader = function FormHeader({
  title,
  subtitle,
  children,
  className = '',
  testId
}) {

  const headerClasses = [
    'arbiwin-form-header',
    className
  ].filter(Boolean).join(' ');

  if (children) {
    return (
      <div
        className={headerClasses}
        data-testid={testId || 'arbiwin-form-header'}
      >
        {children}
      </div>
    );
  }

  return (
    <div
      className={headerClasses}
      data-testid={testId || 'arbiwin-form-header'}
    >
      {title && (
        <h2 className="arbiwin-form-header__title">
          {title}
        </h2>
      )}
      {subtitle && (
        <p className="arbiwin-form-header__subtitle">
          {subtitle}
        </p>
      )}
    </div>
  );
};

/**
 * FormFooter component for form links and additional information
 *
 * @param {Object} props - FormFooter properties
 * @param {React.ReactNode} props.children - Footer content
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.testId - Test ID for testing
 * @returns {JSX.Element} FormFooter component
 */
const FormFooter = function FormFooter({
  children,
  className = '',
  testId
}) {

  const footerClasses = [
    'arbiwin-form-footer',
    className
  ].filter(Boolean).join(' ');

  return (
    <div
      className={footerClasses}
      data-testid={testId || 'arbiwin-form-footer'}
    >
      {children}
    </div>
  );
};

/**
 * FormActions component for form buttons
 *
 * @param {Object} props - FormActions properties
 * @param {React.ReactNode} props.children - Action buttons
 * @param {string} props.align - Button alignment: 'left', 'center', 'right', 'space-between'
 * @param {boolean} props.fullWidth - Make buttons full width
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.testId - Test ID for testing
 * @returns {JSX.Element} FormActions component
 */
const FormActions = function FormActions({
  children,
  align = 'right',
  fullWidth = false,
  className = '',
  testId
}) {

  const actionsClasses = [
    'arbiwin-form-actions',
    'arbiwin-form-actions--' + align,
    fullWidth ? 'arbiwin-form-actions--full-width' : '',
    className
  ].filter(Boolean).join(' ');

  return (
    <div
      className={actionsClasses}
      data-testid={testId || 'arbiwin-form-actions'}
    >
      {children}
    </div>
  );
};

/**
 * FormSuccess component for success messages
 *
 * @param {Object} props - FormSuccess properties
 * @param {string} props.title - Success title
 * @param {string} props.message - Success message
 * @param {React.ReactNode} props.children - Custom success content
 * @param {React.ReactNode} props.actions - Action buttons
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.testId - Test ID for testing
 * @returns {JSX.Element} FormSuccess component
 */
const FormSuccess = function FormSuccess({
  title = 'Успешно!',
  message,
  children,
  actions,
  className = '',
  testId
}) {

  const successClasses = [
    'arbiwin-form-success',
    className
  ].filter(Boolean).join(' ');

  return (
    <div
      className={successClasses}
      data-testid={testId || 'arbiwin-form-success'}
      role="status"
      aria-live="polite"
    >
      <svg
        className="arbiwin-form-success__icon"
        viewBox="0 0 50 50"
        fill="currentColor"
        aria-hidden="true"
      >
        <circle cx="25" cy="25" r="25" fill="currentColor" />
        <path
          d="M38 15L22 31l-8-8"
          stroke="white"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </svg>

      {children ? (
        children
      ) : (
        <>
          <h3 className="arbiwin-form-success__title">{title}</h3>
          {message && (
            <p className="arbiwin-form-success__message">{message}</p>
          )}
        </>
      )}

      {actions && (
        <div className="arbiwin-form-success__actions">
          {actions}
        </div>
      )}
    </div>
  );
};

/**
 * RegistrationForm component - Pre-built registration form
 * Based on the provided registration design
 *
 * @param {Object} props - RegistrationForm properties
 * @param {Function} props.onSubmit - Form submit handler
 * @param {boolean} props.loading - Show loading state
 * @param {Object} props.errors - Form errors object
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} RegistrationForm component
 */
const RegistrationForm = function RegistrationForm({
  onSubmit,
  loading = false,
  errors = {},
  className = ''
}) {

  return (
    <FormContainer>
      <FormHeader
        title="Create Your Account"
        subtitle="Join ArbiWin.bg to get access to real-time market analysis."
      />

      <Form onSubmit={onSubmit} loading={loading} className={className}>
        <div className="arbiwin-form__fields">
          {/* Fields would be added by the consumer */}
        </div>

        <FormActions align="center" fullWidth>
          {/* Actions would be added by the consumer */}
        </FormActions>
      </Form>

      <FormFooter>
        <p className="arbiwin-form-footer__text">
          Already have an account?{' '}
          <a href="#" className="arbiwin-form-footer__link">
            Log In
          </a>
        </p>
      </FormFooter>
    </FormContainer>
  );
};

Form.displayName = 'Form';
FormContainer.displayName = 'FormContainer';
FormHeader.displayName = 'FormHeader';
FormFooter.displayName = 'FormFooter';
FormActions.displayName = 'FormActions';
FormSuccess.displayName = 'FormSuccess';
RegistrationForm.displayName = 'RegistrationForm';

export default Form;
export {
  FormContainer,
  FormHeader,
  FormFooter,
  FormActions,
  FormSuccess,
  RegistrationForm
};
